import { useQuery } from '@tanstack/react-query';

import { listWorkspaceFiles } from '@/lib/api/generated/api';
import type { FileEntry, FileListing } from '@/lib/api/generated/model';
import { parentFolderOf } from '../filePreviewLink';
import { isFolderPlaceholder } from '../folders';
import { fileKeys } from '../queryKeys';
import { reportFileReadFailure } from '../reportFileReadFailure';

/** What resolving a path to its entry can say (spec 140 FR-070, FR-065). */
export interface WorkspaceFileEntry {
  /**
   * The entry, set **only** on a match.
   *
   * Never a partial or a guess, which is what lets a consumer use it directly
   * without re-checking that it is the file it asked for.
   */
  entry: FileEntry | undefined;
  /**
   * The resolved listing, for a consumer that wants to say something about the
   * folder a missing file was looked for in.
   */
  entries: FileEntry[] | undefined;
  /** Still out. Not an answer: the file may yet be there. */
  isPending: boolean;
  /**
   * The read itself failed — the request never came back, or the service
   * answered with something other than an answer.
   *
   * Deliberately a separate field rather than folded into the absence of
   * `entry`: "we could not look" and "we looked and it is not there" are
   * different things to tell a reader, and only one of them is worth a retry.
   * Conflating them would offer a "try again" to a path that was renamed, or
   * report a backend outage as a missing file (FR-065).
   */
  isError: boolean;
  /**
   * Ask again.
   *
   * Part of the result rather than left to the consumer because the read runs
   * with `retry: false`: one failure is final until a reader asks, so the
   * affordance to ask is part of the failed state, not a nicety beside it
   * (spec 140 FR-065).
   */
  refetch: () => void;
  /**
   * A read is in flight right now — including a retry of a failed one.
   *
   * What lets a retry control say it is working instead of looking broken for
   * exactly as long as the read that already failed took.
   */
  isFetching: boolean;
}

/**
 * Resolves a workspace path to its `FileEntry` through the parent folder's
 * listing (spec 140 FR-070).
 *
 * Owns, in one place, the three things a consumer otherwise re-implements: the
 * parent-folder derivation, the listing read under the file browser's own
 * `fileKeys.listing` key, and the entry lookup. It exists because the shape of
 * this cache entry was being decided in two modules at once — whichever query
 * landed first decided what the other one read, which only shows up as a
 * listing that disagrees with itself on a cold deep link.
 *
 * No new request on the warm path, and no new endpoint: the key is the same one
 * `useWorkspaceFileExistence` already populates for exactly these paths while
 * deciding whether a path in prose was a link at all. The folder-placeholder
 * filter is applied here for the same reason — the two queries share the entry,
 * so they must agree on what is in it.
 *
 * A folder the service will not resolve is an answer, not a failure: `422` (a
 * path that is not a directory) and `404` (one that is not there) both mean "no
 * such file", so they resolve to an empty result rather than `isError`.
 *
 * `path` is optional so a consumer can call this unconditionally while nothing
 * is selected — the read is simply not made, and the result reads as pending.
 */
export function useWorkspaceFileEntry(
  workspaceId: string,
  path: string | undefined,
): WorkspaceFileEntry {
  const folder = path === undefined ? undefined : parentFolderOf(path);

  const listing = useQuery({
    queryKey: fileKeys.listing(workspaceId, folder ?? ''),
    queryFn: async ({ signal }): Promise<FileListing | null> => {
      /*
        Carried out of the `try` so the one report below can still name what
        the server answered, exactly as `useFileObjectUrl` does. Absent while
        there has been no response at all — which is itself the diagnosis.
      */
      let status: number | undefined;
      try {
        const response = await listWorkspaceFiles(
          workspaceId,
          { path: folder ?? '' },
          { signal },
        );
        status = response.status;
        if (response.status === 422 || response.status === 404) return null;
        if (response.status !== 200) {
          throw new Error(`Could not read “${folder ?? ''}”.`);
        }
        return {
          ...response.data,
          entries: response.data.entries.filter(
            (entry) => !isFolderPlaceholder(entry),
          ),
        };
      } catch (error) {
        /*
          One `catch` around the whole body, and the only place this path
          reports (F16, F27).

          Reporting used to sit inline in the non-200 branch, which left the
          *rejecting* read — transport failure, DNS, CORS, or
          `response.data.entries` not being the array the contract promises —
          propagating with no console line and no Sentry event at all: the
          panel's generic retry state was its only trace, and that is the most
          likely listing failure in production. Moving the report here covers
          both classes with one call, so neither can be reported twice nor
          described two ways.
        */
        /*
          And only while the answer is still wanted (F28).
          `reportFileReadFailure`'s own contract is that callers report only
          reads someone is waiting for — `useFileObjectUrl` honours it behind
          its `live` flag, and this path did not. A `queryFn` re-runs on every
          refetch and on every remount of a panel that was opened, closed and
          reopened, so without this guard a preview abandoned mid-flight burns
          an event for an answer nobody will see. `signal` is the liveness
          check the query context already hands us: TanStack aborts it exactly
          when the observer that asked has gone away, which is also what makes
          the abort's own rejection silent here rather than reported as a
          backend fault.
        */
        if (!signal.aborted) {
          reportFileReadFailure(
            'listing',
            { workspaceId, folder: folder ?? '', status },
            error,
          );
        }
        throw error;
      }
    },
    retry: false,
    enabled: path !== undefined,
  });

  // With no path there is nothing resolving and nothing resolved. TanStack
  // reports a disabled query as pending, which is the honest reading here too:
  // the caller has not asked a question yet.
  const isPending = path === undefined || listing.isPending;
  const entries =
    isPending || listing.isError ? undefined : (listing.data?.entries ?? []);

  return {
    entry: entries?.find(
      (candidate) => candidate.path === path && candidate.kind === 'file',
    ),
    entries,
    isPending,
    isError: !isPending && listing.isError,
    refetch: () => void listing.refetch(),
    isFetching: listing.isFetching,
  };
}
